import { useState } from 'react';
import { Download, FileJson, Copy, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { PricingRule, AvailabilityRule, Property, Event } from '@/types/revenue-manager';

type ExportableRule = PricingRule | AvailabilityRule | Event;

interface ExportRulesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rules: ExportableRule[];
  ruleType: 'pricing' | 'availability' | 'events';
  property: Property;
  orgId?: string | number;
}

export function ExportRulesDialog({
  open,
  onOpenChange,
  rules,
  ruleType,
  property,
  orgId,
}: ExportRulesDialogProps) {
  const [selectedRules, setSelectedRules] = useState<string[]>(rules.map(r => r.id));
  const [copied, setCopied] = useState(false);

  const toggleRule = (ruleId: string) => {
    setSelectedRules((prev) =>
      prev.includes(ruleId)
        ? prev.filter((id) => id !== ruleId)
        : [...prev, ruleId]
    );
  };

  const toggleAll = () => {
    if (selectedRules.length === rules.length) {
      setSelectedRules([]);
    } else {
      setSelectedRules(rules.map(r => r.id));
    }
  };

  const exportData = {
    exportedAt: new Date().toISOString(),
    sourceProperty: property.name,
    ruleType,
    org_id: orgId ? String(orgId) : undefined,
    rules: rules.filter(r => selectedRules.includes(r.id)).map((rule) => {
      // Remove id and timestamps if they exist
      const { id, ...rest } = rule;
      if ('createdAt' in rest) delete (rest as any).createdAt;
      if ('updatedAt' in rest) delete (rest as any).updatedAt;
      
      // Add org_id and applicable_cm_names to each rule
      return {
        ...rest,
        org_id: orgId ? String(orgId) : undefined,
        applicable_cm_names: (rule as any).applicateCmNames || [],
      };
    }),
  };

  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${ruleType}-rules-${property.name.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    onOpenChange(false);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(JSON.stringify(exportData, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export {ruleType === 'pricing' ? 'Pricing Rules' : ruleType === 'availability' ? 'Availability Rules' : 'Events'}
          </DialogTitle>
          <DialogDescription>
            Export {ruleType === 'events' ? 'events' : 'rules'} from {property.name} to apply to other properties
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="flex items-center justify-between">
            <Label>Select {ruleType === 'events' ? 'Events' : 'Rules'} to Export</Label>
            <Button variant="ghost" size="sm" onClick={toggleAll}>
              {selectedRules.length === rules.length ? 'Deselect All' : 'Select All'}
            </Button>
          </div>

          {rules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No {ruleType === 'events' ? 'events' : 'rules'} to export
            </div>
          ) : (
            <div className="rounded-lg border border-border max-h-64 overflow-y-auto">
              {rules.map((rule) => (
                <div
                  key={rule.id}
                  className={`flex items-center gap-3 p-3 border-b border-border last:border-0 cursor-pointer transition-colors ${
                    selectedRules.includes(rule.id) ? 'bg-accent/10' : 'hover:bg-muted'
                  }`}
                  onClick={() => toggleRule(rule.id)}
                >
                  <Checkbox
                    checked={selectedRules.includes(rule.id)}
                    onCheckedChange={() => toggleRule(rule.id)}
                  />
                  <div className="flex-1">
                    <p className="font-medium">{rule.name}</p>
                    {'description' in rule && rule.description && (
                      <p className="text-sm text-muted-foreground">{rule.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-lg border border-border bg-muted/50 p-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              <FileJson className="h-4 w-4" />
              Export Preview
            </div>
            <pre className="text-xs overflow-auto max-h-32 font-mono">
              {JSON.stringify({ rulesCount: selectedRules.length, ruleType }, null, 2)}
            </pre>
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={handleCopy} disabled={selectedRules.length === 0}>
            {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
            {copied ? 'Copied!' : 'Copy JSON'}
          </Button>
          <Button onClick={handleDownload} disabled={selectedRules.length === 0}>
            <Download className="mr-2 h-4 w-4" />
            Download File
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
